<script setup lang="ts">
import type { IEquipmentUpsertForm } from '@/types/hvl/forms'
import { type FormInstance, type FormRules } from 'element-plus'
import { reactive, ref } from 'vue'
import type { IEquipment } from '@/types/hvl/Equipment'
import { useEquipment } from '@/composables/hvl/equipment'
import type { ISiteIdentifier } from '../rc/common/RcSiteSelect.vue'
import RcSiteSelect from '../rc/common/RcSiteSelect.vue'
import HvlEquipmentTypeSelect from './common/HvlEquipmentTypeSelect.vue'
import type { IEquipmentType } from '@/types/hvl/EquipmentType'

const props = defineProps<{ equipment?: IEquipment }>()

const model = defineModel<boolean>()

const selectedEquipmentTypeRef = ref<IEquipmentType>()
const selectedSiteRef = ref<ISiteIdentifier>()
const equipmentFormRef = ref<FormInstance>()
const equipmentForm = reactive<IEquipmentUpsertForm>({
  name: undefined,
  site_id: undefined,
  type_id: undefined,
  location: undefined,
})

const rules = reactive<FormRules<IEquipmentUpsertForm>>({
  name: [{ required: true, message: 'Name is required.', trigger: ['blur', 'change'] }],
  location: [{ required: true, message: 'Location is required.', trigger: ['blur', 'change'] }],
  type_id: [{ required: true, message: 'Type is required.', trigger: ['blur', 'change'] }],
  site_id: [{ required: true, message: 'Site is required.', trigger: ['blur', 'change'] }],
})

const { createEquipmentMutation, updateEquipmentMutation } = useEquipment()

const { mutateAsync: createMutation, isPending: createIsPending } = createEquipmentMutation
const { mutateAsync: updateMutation, isPending: updateIsPending } = updateEquipmentMutation

const initForm = async () => {
  if (!props.equipment) {
    equipmentForm.site_id = undefined
    equipmentForm.name = undefined
    equipmentForm.location = undefined
    equipmentForm.type_id = undefined
    return
  }

  equipmentForm.site_id = props.equipment.site.site_id
  equipmentForm.name = props.equipment.name
  equipmentForm.location = props.equipment.location
  equipmentForm.type_id = props.equipment.type.id

  selectedEquipmentTypeRef.value = {
    id: props.equipment.type.id,
    name: props.equipment.type.name,
    inspection_points: [],
    failure_types: [],
    repair_actions: [],
  }

  selectedSiteRef.value = {
    site_id: props.equipment.site.site_id,
    site_name: props.equipment.site.name,
  }
}

/**
 * Handles form submit.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formEl?.validate(async (valid, fields) => {
    if (valid) {
      try {
        if (props.equipment) {
          await updateMutation({
            id: props.equipment.id,
            form: equipmentForm,
          })
        } else {
          await createMutation(equipmentForm)
        }
        resetFormCloseDialog()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_: any) {
        // Error messages handled by composable
      }
    }
  })
}

const onSiteChange = (val: any) => {
  if (val) {
    selectedSiteRef.value = val
    equipmentForm.site_id = val.site_id
    return
  }

  equipmentForm.site_id = undefined
  selectedSiteRef.value = undefined
}

const onEquipmentTypeChange = (val: any) => {
  if (val) {
    selectedEquipmentTypeRef.value = val
    equipmentForm.type_id = val.id
    return
  }

  equipmentForm.type_id = undefined
  selectedEquipmentTypeRef.value = undefined
}

/**
 * Resets form, closes dialog.
 */
const resetFormCloseDialog = () => {
  equipmentFormRef.value?.resetFields()
  selectedSiteRef.value = undefined
  selectedEquipmentTypeRef.value = undefined
  model.value = false
}
</script>

<template>
  <el-dialog
    v-model="model"
    :title="`${equipment ? 'Update' : 'Create'} Equipment`"
    :show-close="false"
    :width="480"
    @open="initForm"
    @closed="resetFormCloseDialog"
  >
    <el-form ref="equipmentFormRef" :model="equipmentForm" :rules="rules" label-width="auto" label-position="left">
      <el-form-item label="Name" prop="name">
        <el-input v-model="equipmentForm.name" placeholder="Name" clearable />
      </el-form-item>
      <el-form-item label="Type" prop="type_id">
        <HvlEquipmentTypeSelect v-model="selectedEquipmentTypeRef" placeholder="Type" @change="onEquipmentTypeChange" />
      </el-form-item>
      <el-form-item label="Site" prop="site_id">
        <RcSiteSelect v-model="selectedSiteRef" placeholder="Site" @change="onSiteChange" />
      </el-form-item>
      <el-form-item label="Location" prop="location">
        <el-input v-model="equipmentForm.location" placeholder="Location" clearable />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" :loading="createIsPending || updateIsPending" @click="submitForm(equipmentFormRef)"
        >Submit</el-button
      >
    </template>
  </el-dialog>
</template>
